import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect } from 'react';
/**
 * TurnstileWidget Component - Cloudflare Turnstile CAPTCHA
 *
 * A reusable component for integrating Cloudflare Turnstile CAPTCHA challenges.
 *
 * @example
 * ```tsx
 * <TurnstileWidget
 *   siteKey="your-site-key"
 *   onTokenChange={(token) => setTurnstileToken(token)}
 *   errorMessage={formErrors.turnstile}
 * />
 * ```
 */
export const TurnstileWidget = ({ siteKey, onTokenChange, errorMessage, className = '', errorClassName = '', resetKey = 0, }) => {
    const turnstileRef = React.useRef(null);
    const widgetIdRef = React.useRef(null);
    const hasInitializedRef = React.useRef(false);
    useEffect(() => {
        if (widgetIdRef.current && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
            onTokenChange('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey]);
    useEffect(() => {
        // Prevent double initialization
        if (hasInitializedRef.current)
            return;
        const initializeTurnstile = () => {
            if (turnstileRef.current && window.turnstile && !widgetIdRef.current) {
                try {
                    const widgetId = window.turnstile.render(turnstileRef.current, {
                        sitekey: siteKey,
                        theme: 'light',
                        callback: (token) => {
                            onTokenChange(token);
                        },
                        'error-callback': () => {
                            onTokenChange('');
                        },
                        'expired-callback': () => {
                            onTokenChange('');
                            if (widgetIdRef.current && window.turnstile) {
                                window.turnstile.reset(widgetIdRef.current);
                            }
                        },
                    });
                    widgetIdRef.current = widgetId;
                    hasInitializedRef.current = true;
                }
                catch (error) {
                    console.error('[Turnstile] Failed to render widget:', error);
                }
            }
        };
        // Load Turnstile script if not already loaded
        if (!window.turnstile) {
            const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
            if (existingScript) {
                // Script is loading, wait for it
                existingScript.addEventListener('load', initializeTurnstile);
                return () => {
                    existingScript.removeEventListener('load', initializeTurnstile);
                };
            }
            const script = document.createElement('script');
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
            script.async = true;
            script.defer = true;
            script.onload = initializeTurnstile;
            document.head.appendChild(script);
        }
        else {
            // Script already loaded
            initializeTurnstile();
        }
        return () => {
            // Cleanup: remove widget on unmount
            if (widgetIdRef.current && window.turnstile) {
                try {
                    window.turnstile.remove(widgetIdRef.current);
                }
                catch (error) {
                    console.warn('[Turnstile] Error removing widget:', error);
                }
                widgetIdRef.current = null;
            }
            hasInitializedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (_jsxs("div", { className: className, children: [_jsx("div", { ref: turnstileRef }), errorMessage && (_jsx("p", { className: errorClassName, style: { color: '#d32f2f', marginTop: '4px', fontSize: '14px' }, children: errorMessage }))] }));
};
